import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { renameKeysFromSDKRequestToRESTRequest } from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient } from '@wix/sdk-types';
import * as ambassadorWixDevcenterBiEventsV1SendBiEventResponse from './devcenter-bi-events-v1-send-bi-event-response-bi-events.http.js';

export interface SendBIEventResponse {}

export interface SendBIEventRequest {
  /** Name of the event that's triggered in your app. */
  eventName?: EventNameWithLiterals;
  /**
   * Name of your app's custom event that was triggered. Required when `{"eventName": "CUSTOM"}`.
   *
   * Min: 2 characters
   * @minLength 2
   */
  customEventName?: string | null;
  /**
   * Supported values include: `"cycle_name"`, `"currency"`, `"sum"`, `"reason"`, `"app_plan_id"`.
   * You may also submit data with keys that aren't listed here.
   *
   * Additional data about your app's event.
   *
   * + `"cycle_name"`: Supported values: `"monthly"`, `"yearly"`, `"2 years"`, `"one time"`.
   * + `"currency"`: 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * + `"sum"`: Amount of money. Required for `{"eventName": "CHARGE"}` and `{"eventName": "FUNDS_RETURNED"}`.
   * + `"reason"`: Information about why the event was triggered in your app.
   * + `"app_plan_id"`: ID of the your app's plan as displayed in your app's dashboard. Note that it's the same value as `vendorProductId` in the [Paid Plan Purchased webhook](https://dev.wix.com/docs/rest/app-management/app-instance/paid-plan-purchased).
   */
  eventData?: Record<string, string>;
}

export enum EventName {
  /** Unknown event name. */
  UNKNOWN = 'UNKNOWN',
  /** A Wix user loads your app's dashboard. */
  APP_DASHBOARD_LOADED = 'APP_DASHBOARD_LOADED',
  /** The Wix user completes all required configurations for your app in your app's dashboard. */
  APP_FINISHED_CONFIGURATION = 'APP_FINISHED_CONFIGURATION',
  /** The Wix user upgrades your app's paid plan. An upgrade means that they have finished the checkout flow on the app's side but not necessarily on Wix's side. */
  APP_UPGRADED = 'APP_UPGRADED',
  /** A site owner, contributor, or visitor triggers your app's primary action. For example, a site visitor writes a product review using your product review app. */
  PRIMARY_ACTION_PERFORMED = 'PRIMARY_ACTION_PERFORMED',
  /** Any event that's not listed here. Make sure to also send `customEventName`. */
  CUSTOM = 'CUSTOM',
  /** You charge money from the Wix user. For example, when the Wix user purchases or renews a subscription for your app. Make sure to also send `eventData` and a key of `sum`. */
  CHARGE = 'CHARGE',
  /** Trigger this event when you send money back to a Wix user (for example, refunds or chargebacks). Make sure to also send the charge amount inside `eventData`. For example, `{"eventData": {"sum": "5.99"}}`. */
  FUNDS_RETURNED = 'FUNDS_RETURNED',
  /** __Deprecation Notice:__ This enum value will be removed on March 30, 2023. Use `"APP_SETUP_FINISHED"` instead. */
  APP_FINISH_BUSINESS_SETUP = 'APP_FINISH_BUSINESS_SETUP',
  /** Your app’s internal code implementation is changed and might affect user flows or cause a regression. */
  APP_DEPLOYED = 'APP_DEPLOYED',
  /** The Wix user completes your app's required business setup, including in the dashboard and editor. */
  APP_SETUP_FINISHED = 'APP_SETUP_FINISHED',
}

/** @enumType */
export type EventNameWithLiterals =
  | EventName
  | 'UNKNOWN'
  | 'APP_DASHBOARD_LOADED'
  | 'APP_FINISHED_CONFIGURATION'
  | 'APP_UPGRADED'
  | 'PRIMARY_ACTION_PERFORMED'
  | 'CUSTOM'
  | 'CHARGE'
  | 'FUNDS_RETURNED'
  | 'APP_FINISH_BUSINESS_SETUP'
  | 'APP_DEPLOYED'
  | 'APP_SETUP_FINISHED';

/**
 * Submit a BI event to Wix.
 * @public
 * @permissionId WIX_DEVELOPERS.SEND_BI_EVENTS
 * @applicableIdentity APP
 * @fqn com.wixpress.market.aim.api.BIEventsService.sendBIEvent
 */
export async function sendBiEvent(options?: SendBiEventOptions): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventName: options?.eventName,
    customEventName: options?.customEventName,
    eventData: options?.eventData,
  });

  const reqOpts =
    ambassadorWixDevcenterBiEventsV1SendBiEventResponse.sendBiEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventName: '$[0].eventName',
          customEventName: '$[0].customEventName',
          eventData: '$[0].eventData',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SendBiEventOptions {
  /** Name of the event that's triggered in your app. */
  eventName?: EventNameWithLiterals;
  /**
   * Name of your app's custom event that was triggered. Required when `{"eventName": "CUSTOM"}`.
   *
   * Min: 2 characters
   * @minLength 2
   */
  customEventName?: string | null;
  /**
   * Supported values include: `"cycle_name"`, `"currency"`, `"sum"`, `"reason"`, `"app_plan_id"`.
   * You may also submit data with keys that aren't listed here.
   *
   * Additional data about your app's event.
   *
   * + `"cycle_name"`: Supported values: `"monthly"`, `"yearly"`, `"2 years"`, `"one time"`.
   * + `"currency"`: 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * + `"sum"`: Amount of money. Required for `{"eventName": "CHARGE"}` and `{"eventName": "FUNDS_RETURNED"}`.
   * + `"reason"`: Information about why the event was triggered in your app.
   * + `"app_plan_id"`: ID of the your app's plan as displayed in your app's dashboard. Note that it's the same value as `vendorProductId` in the [Paid Plan Purchased webhook](https://dev.wix.com/docs/rest/app-management/app-instance/paid-plan-purchased).
   */
  eventData?: Record<string, string>;
}
